import { type Address } from 'viem';
import { useReadContract } from 'wagmi';
import {
  ERC20_ABI,
  SWAP_ABI,
  type SwapStatistics,
  type UserInfo,
} from '../constants';

/**
 * Hook for all Tokenswap contract read operations
 * @param requiredChainId - Dynamic chainId from backend config (BSC or Sepolia)
 * @param oldFarToken - Dynamic old FAR token address from backend config
 * @param newFarToken - Dynamic new FAR token address from backend config
 * @param swapContract - Dynamic swap contract address from backend config
 */
export const useContractReads = (
  address: Address | undefined,
  chainId: number | undefined,
  requiredChainId: number,
  oldFarToken: Address,
  newFarToken: Address,
  swapContract: Address
) => {
  // Read OLD token balance
  const { data: oldTokenBalance, refetch: refetchOldBalance } = useReadContract({
    address: oldFarToken,
    abi: ERC20_ABI,
    functionName: 'balanceOf',
    args: address ? [address] : undefined,
    query: {
      enabled: !!address && chainId === requiredChainId,
      refetchInterval: 5000, // Refresh every 5 seconds
    },
  });

  // Read NEW token balance
  const { data: newTokenBalance, refetch: refetchNewBalance } = useReadContract({
    address: newFarToken,
    abi: ERC20_ABI,
    functionName: 'balanceOf',
    args: address ? [address] : undefined,
    query: {
      enabled: !!address && chainId === requiredChainId,
      refetchInterval: 5000,
    },
  });

  // Read token allowance
  const { data: tokenAllowance, refetch: refetchAllowance } = useReadContract({
    address: oldFarToken,
    abi: ERC20_ABI,
    functionName: 'allowance',
    args: address ? [address, swapContract] : undefined,
    query: {
      enabled: !!address && chainId === requiredChainId,
      refetchInterval: 3000,
    },
  });

  // Read swap statistics
  const { data: statistics, refetch: refetchStats } = useReadContract({
    address: swapContract,
    abi: SWAP_ABI,
    functionName: 'getStatistics',
    query: {
      enabled: chainId === requiredChainId,
      refetchInterval: 10000,
    },
  }) as { data: SwapStatistics | undefined; refetch: () => void };

  // Read swap enabled status
  const { data: swapActive } = useReadContract({
    address: swapContract,
    abi: SWAP_ABI,
    functionName: 'swapActive',
    query: {
      enabled: chainId === requiredChainId,
      refetchInterval: 5000,
    },
  });

  // Read user swap info
  const { data: userInfo, refetch: refetchUserInfo } = useReadContract({
    address: swapContract,
    abi: SWAP_ABI,
    functionName: 'getUserInfo',
    args: address ? [address] : undefined,
    query: {
      enabled: !!address && chainId === requiredChainId,
      refetchInterval: 10000,
    },
  }) as { data: UserInfo | undefined; refetch: () => void };

  return {
    oldTokenBalance,
    newTokenBalance,
    tokenAllowance,
    statistics,
    swapActive,
    userInfo,
    refetchOldBalance,
    refetchNewBalance,
    refetchAllowance,
    refetchStats,
    refetchUserInfo,
  };
};
